import type { ReactNode } from 'react';

import { create } from 'zustand';

export const confirmDialogVariants = {
    default: {
        cancelVariant: 'outline',
        confirmVariant: 'default',
    },
    warning: {
        cancelVariant: 'destructive',
        confirmVariant: 'default',
    },
    destructive: {
        cancelVariant: 'outline',
        confirmVariant: 'destructive',
    },
    // The solid destructive button — `danger` is solid, `destructive` is the muted variant.
    danger: {
        cancelVariant: 'outline',
        confirmVariant: 'danger',
    },
    neutral: {
        cancelVariant: 'outline',
        confirmVariant: 'outline',
    },
} as const;

type ConfirmDialogProps = {
    title: string;
    description?: ReactNode | string;
    cancelText?: string;
    confirmText?: string;
    variant?: keyof typeof confirmDialogVariants;
    onConfirm: () => void;
    onCancel?: () => void;
    onClose?: () => void;
    width?: '2xs' | 'xs' | 'sm' | 'md' | 'lg';
};

type ConfirmDialogState = {
    props?: ConfirmDialogProps;
    isOpen: boolean;
    open: (props: ConfirmDialogProps) => void;
    close: (confirm: boolean) => void;
    dismiss: () => void;
};

const resetProps = (props: ConfirmDialogProps | undefined): ConfirmDialogProps => ({
    ...props,
    title: props?.title || '',
    onConfirm: () => {},
    onCancel: undefined,
    onClose: undefined,
});

export const useConfirmDialogStore = create<ConfirmDialogState>((set, get) => ({
    isOpen: false,
    open: (props) => set({ isOpen: true, props }),
    close: (confirm) => {
        const { props } = get();
        if (confirm) {
            props?.onConfirm();
        } else {
            props?.onCancel?.();
        }
        props?.onClose?.();
        set({
            isOpen: false,
            props: resetProps(props),
        });
    },
    dismiss: () =>
        set({
            isOpen: false,
            props: resetProps(get().props),
        }),
}));
